import * as species from "../../utils/SpeciesScope";

import { Button, Conditional, Input, Modal } from "@result/rui/components";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useRemoteStorage from "#/hooks/useRemoteStorage";

const OPERATION_TYPE = {
  NONE: "NONE",
  EDIT: "EDIT",
  DELETE: "DELETE",
};

const BreedingValuesPresets = ({ storageKey, values, onLoad }) => {
  const { t } = useTranslation();

  const supportedSpecies = species.get();
  const currentSpecies = supportedSpecies?.sort()?.[0];
  const [storedPresets, setStoredPresets] = useRemoteStorage(
    storageKey + (currentSpecies ? "_" + currentSpecies : ""),
    null
  );

  const [currentOperation, setCurrentOperation] = useState(OPERATION_TYPE.NONE);
  const [upsertOptions, setUpsertOptions] = useState({
    isActive: false,
    upsertIndex: null,
    nameInputValue: "",
  });

  const presets = useMemo(() => {
    if (storedPresets) {
      return storedPresets;
    }
    return [];
  }, [storedPresets]);

  const updatePresets = (newPresets) => {
    setStoredPresets(newPresets);
    setUpsertOptions({
      isActive: false,
    });
    setCurrentOperation(OPERATION_TYPE.NONE);
  };

  return (
    <div className="box breeding-values-presets">
      <div className="is-flex mb-2">
        <div className="is-size-4 m-0">{t("breedingValues.savedPresets")}</div>
        <div className="ml-2 is-flex is-gap-1">
          <Conditional condition={presets.length > 0}>
            <Conditional condition={currentOperation !== OPERATION_TYPE.NONE}>
              <Button
                icon="icon-xmark"
                onClick={() => {
                  setCurrentOperation(OPERATION_TYPE.NONE);
                }}
              />
            </Conditional>
            <Conditional condition={currentOperation === OPERATION_TYPE.NONE}>
              <Button
                icon="icon-pencil"
                onClick={() => {
                  setCurrentOperation(OPERATION_TYPE.EDIT);
                }}
              />
              <Button
                icon="icon-trash"
                onClick={() => {
                  setCurrentOperation(OPERATION_TYPE.DELETE);
                }}
              />
            </Conditional>
          </Conditional>
        </div>
      </div>

      <div className="is-flex is-gap-2 mb-2">
        {presets.map((preset, index) => {
          let icon = undefined;
          let color = undefined;
          let onClick = () => onLoad(preset.values ?? {});
          if (currentOperation === OPERATION_TYPE.EDIT) {
            icon = "icon-pencil";
            color = "primary";
            onClick = () => {
              setUpsertOptions({
                isActive: true,
                upsertIndex: index,
                nameInputValue: preset.name,
              });
            };
          } else if (currentOperation === OPERATION_TYPE.DELETE) {
            icon = "icon-trash";
            color = "danger";
            onClick = () => {
              updatePresets(presets.filter((_, i) => i !== index));
            };
          }
          return (
            <div key={index} className="is-flex">
              <Button
                label={preset.name}
                onClick={onClick}
                rightIcon={icon}
                color={color}
              />
            </div>
          );
        })}
        <Button
          icon="icon-plus"
          onClick={() => setUpsertOptions({ isActive: true })}
        />
      </div>
      <Modal
        isActive={upsertOptions.isActive}
        title={t("breedingValues.savedPresets.setNameForPreset")}
        onClose={() => setUpsertOptions({ isActive: false })}
        footerElement={
          <div className="buttons is-right">
            <Button
              label={t("form.save")}
              color={"primary"}
              isDisabled={
                upsertOptions.nameInputValue === "" ||
                upsertOptions.nameInputValue == null
              }
              onClick={() => {
                if (upsertOptions.upsertIndex == null) {
                  updatePresets([
                    ...presets,
                    {
                      name: upsertOptions.nameInputValue,
                      values: values,
                    },
                  ]);
                  setUpsertOptions({ isActive: false });
                } else {
                  const newPresets = [...presets];
                  newPresets[upsertOptions.upsertIndex] = {
                    name: upsertOptions.nameInputValue,
                    values: values,
                  };
                  updatePresets(newPresets);
                }
              }}
            />
            <Button
              label={t("form.cancel")}
              onClick={() => {
                setUpsertOptions({ isActive: false });
                setCurrentOperation(OPERATION_TYPE.NONE);
              }}
            />
          </div>
        }>
        <Input
          label={t("breedingValues.savedPresets.name")}
          value={upsertOptions.nameInputValue}
          onChange={(value) => {
            setUpsertOptions({
              ...upsertOptions,
              nameInputValue: value,
            });
          }}
        />
      </Modal>
    </div>
  );
};

export default BreedingValuesPresets;
